import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/Button/Button';
import { FormInput } from '../../../ui/FormInput/FormInput';
import { ValidationMessage } from '../../../components/Forms/ValidationMessage';
import { relanceMinimumAmountService } from '../../../services/relanceMinimumAmountService';
import { useNoAiStore } from '../../../stores/noAiStore';
import { TranslationKey } from '../../../i18n/translationKeys';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// Digits only, a comma or a dot, two decimals at most: what the server's numeric(18,2) column can hold.
const AMOUNT_INPUT = /^\d{0,16}([.,]\d{0,2})?$/;

const parseAmount = (value: string): number | null => {
    const amount = Number(value.replace(',', '.'));
    return value.trim() !== '' && Number.isFinite(amount) ? amount : null;
};

// String-based so a 16-digit amount keeps every digit; an empty field or a lone separator reads as 0.
const formatAmount = (value: string): string => {
    const [whole, decimals = ''] = value.split(/[.,]/);
    return `${whole.replace(/^0+(?=\d)/, '') || '0'}.${decimals.padEnd(2, '0')}`;
};

export const RelanceMinimumAmountPanel: React.FC = () => {
    const { t } = useTranslation();
    const noAi = useNoAiStore(s => s.noAi);
    const [loaded, setLoaded] = useState(false);
    const [value, setValue] = useState('');
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const setting = await relanceMinimumAmountService.get();
                if (cancelled) return;
                setValue(setting.minimumAmount.toFixed(2));
                setLoaded(true);
            } catch (e) {
                console.error('Failed to load the relance minimum amount:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!loaded) return null;

    const handleSave = async () => {
        const amount = parseAmount(value);
        setInvalid(amount === null);
        if (amount === null) return;

        setSaveState('saving');
        try {
            await relanceMinimumAmountService.set(amount, noAi);
            setSaveState('saved');
        } catch (e) {
            setSaveState('error');
            console.error('Failed to save the relance minimum amount:', e);
        }
    };

    return (
        <div className="space-y-2">
            <h2 className="font-normal text-primary-1100">{t(TranslationKey.Settings.RelanceMinimumAmount)}</h2>
            <p className="text-primary-700">{t(TranslationKey.Settings.RelanceMinimumAmountDescription)}</p>
            <div className="flex items-start gap-2 pt-1">
                <div>
                    <FormInput
                        id="settings-relance-minimum-amount"
                        data-testid="settings-relance-minimum-amount"
                        className="w-32"
                        type="text"
                        inputMode="decimal"
                        value={value}
                        onChange={(e) => {
                            if (!AMOUNT_INPUT.test(e.target.value)) return;
                            setValue(e.target.value);
                            setSaveState('idle');
                        }}
                        onBlur={() => { setValue(formatAmount(value)); setInvalid(false); }}
                        aria-label={t(TranslationKey.Settings.RelanceMinimumAmount)}
                        aria-invalid={invalid}
                        aria-describedby={invalid ? 'settings-relance-minimum-amount-err' : undefined}
                    />
                    <ValidationMessage
                        id="settings-relance-minimum-amount-err"
                        data-testid="settings-relance-minimum-amount-invalid"
                        message={invalid ? t(TranslationKey.Settings.RelanceMinimumAmountInvalid) : undefined}
                    />
                </div>
                <Button data-testid="settings-relance-minimum-amount-save" onClick={() => void handleSave()} loading={saveState === 'saving'}>
                    {t(TranslationKey.Settings.RelanceMinimumAmountSave)}
                </Button>
            </div>
            {saveState === 'saved' && (
                <p data-testid="settings-relance-minimum-amount-saved" className="text-primary-700 text-s">{t(TranslationKey.Settings.RelanceMinimumAmountSaved)}</p>
            )}
            {saveState === 'error' && (
                <p data-testid="settings-relance-minimum-amount-error" className="text-danger text-s">{t(TranslationKey.Settings.RelanceMinimumAmountError)}</p>
            )}
        </div>
    );
};
